import { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  PermissionAccessProvider, PermissionAwareButton, PermissionAwareInput, PermissionAwareSelect, PermissionAwareTextarea,
} from '../../../contexts/PermissionAccessContext';
import UserPicker from '../../../components/UserPicker';
import safetySystemService from '../../../services/safetySystem.service';
import type { CostLineRow, IncidentLookups, LookupOption } from '../../../types/safetySystem';
import SafetyDocuments from './SafetyDocuments';
import { inputClass, str } from './unitFields';
import { YES_NO_OPTIONS, fromDateInput, toDateInput } from './unitForm';
import { ChildRow } from './ticketForm';
import { DriverTypeahead, UnitTypeahead, driverLabel } from './ticketTypeaheads';
import { errorText } from './safetyDrawerShared';
import { EQUIP_TYPES, GWI_USER_PARTY_TYPES, PARTY_TYPES, applyClaimType, costLinesFor, money, newCostLine, num } from './incidentForm';
import { shortDate } from '../SafetyUnits.columns';

/**
 * Child-row tabs of the Incident drawer: Inv Parties, Equip & Cargo, Claims, Costs (with
 * the claim-cost Lines sub-panel) and Tickets (link / unlink); the History tab is the
 * shared `SafetyHistoryTab` with `INCIDENT_HISTORY_LABELS`. Each editable row is a card of
 * labelled PermissionAware controls ("<Field> <n>") so rows stay reachable by label.
 * Removing an existing row marks it `deleted:'Y'` (posted as a delete marker); removing a
 * new row just drops it.
 */

/** `incident_history.dbtable` -> display label. */
export const INCIDENT_HISTORY_LABELS: Record<string, string> = {
  incident: 'Incident', involved_party: 'Party', incident_equipment: 'Equipment', claim: 'Claim', associatedcost: 'Cost', claimcost: 'Cost line', notes: 'Note',
};

/**
 * Client-only key on a claim row holding the typed / picked driver label, so it survives a
 * tab switch (the tab unmounts). Never posted: `childRowsForSave` copies only the field
 * whitelist.
 */
export const CLAIM_DRIVER_LABEL_KEY = '_driverlabel';

const live = (rows: ChildRow[]) => rows.filter((r) => str(r.deleted).toUpperCase() !== 'Y');
const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1.5 py-1 align-top';
const cell = inputClass;
const labelCls = 'block text-xs font-medium text-muted mb-0.5';

type Opt = { value: string; label: string };
const lookupOpts = (rows: LookupOption[] | undefined): Opt[] => (rows ?? []).map((r) => ({ value: String(r.recno), label: str(r.label) }));

function Cell({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <span className={labelCls}>{label}</span>
      {children}
    </div>
  );
}

function Text({ label, value, onChange, type = 'text', className, maxLength, readOnly }: {
  label: string; value: unknown; onChange: (v: string) => void; type?: string; className?: string; maxLength?: number; readOnly?: boolean;
}) {
  return (
    <Cell label={label} className={className}>
      <PermissionAwareInput type={type} aria-label={label} className={cell} value={str(value)} maxLength={maxLength} readOnly={readOnly} disabled={readOnly}
        onChange={(e) => onChange(e.target.value)} />
    </Cell>
  );
}

function Yn({ label, value, onChange, className }: { label: string; value: unknown; onChange: (v: string) => void; className?: string }) {
  return (
    <Cell label={label} className={className}>
      <PermissionAwareSelect aria-label={label} className={cell} value={str(value)} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {YES_NO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </PermissionAwareSelect>
    </Cell>
  );
}

function Sel({ label, value, onChange, options, className, allowBlank = true }: {
  label: string; value: unknown; onChange: (v: string) => void; options: Opt[]; className?: string; allowBlank?: boolean;
}) {
  const v = str(value);
  const current = v && v !== '0' ? v : '';
  return (
    <Cell label={label} className={className}>
      <PermissionAwareSelect aria-label={label} className={cell} value={current} onChange={(e) => onChange(e.target.value)}>
        {allowBlank && <option value="">—</option>}
        {current && !options.some((o) => o.value === current) && <option value={current}>{current}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </PermissionAwareSelect>
    </Cell>
  );
}

function DateCell({ label, value, onChange, className }: { label: string; value: unknown; onChange: (v: string) => void; className?: string }) {
  const d = toDateInput(value);
  const iso = d ? fromDateInput(d) : '';
  return (
    <Cell label={label} className={className}>
      <PermissionAwareInput type="date" aria-label={label} className={cell} value={iso} onChange={(e) => onChange(e.target.value)} />
    </Cell>
  );
}

function RowCard({ n, title, onRemove, removeLabel, children, extra }: {
  n: number; title: string; onRemove: () => void; removeLabel: string; children: React.ReactNode; extra?: React.ReactNode;
}) {
  return (
    <li className="rounded-md border border-rule p-2">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">{`${n}. ${title}`}</span>
        <PermissionAwareButton type="button" aria-label={removeLabel} onClick={onRemove}
          className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg disabled:cursor-not-allowed">
          <TrashIcon width={16} aria-hidden="true" />
        </PermissionAwareButton>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">{children}</div>
      {extra}
    </li>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <PermissionAwareButton type="button" onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-1 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled">
      <PlusIcon width={14} aria-hidden="true" />{label}
    </PermissionAwareButton>
  );
}

interface RowsTabProps {
  rows: ChildRow[];
  onRowsChange: (rows: ChildRow[]) => void;
  canEdit: boolean;
  onAdd: () => void;
}

const useRowOps = (rows: ChildRow[], onRowsChange: (rows: ChildRow[]) => void) => ({
  update: (key: string, patch: Record<string, unknown> | ((row: ChildRow) => ChildRow)) =>
    onRowsChange(rows.map((r) => (r._key === key ? (typeof patch === 'function' ? patch(r) : { ...r, ...patch }) : r))),
  remove: (key: string) =>
    onRowsChange(rows.flatMap((r) => (r._key !== key ? [r] : r.recno ? [{ ...r, deleted: 'Y' }] : []))),
});

// ---- Involved parties -------------------------------------------------------

export function PartiesTab({ rows, onRowsChange, canEdit, onAdd }: RowsTabProps) {
  const visible = live(rows);
  const { update, remove } = useRowOps(rows, onRowsChange);
  const typeOpts = PARTY_TYPES.map((v) => ({ value: v, label: v }));
  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="space-y-2">
        {visible.length === 0 && <div className="py-4 text-center text-sm text-muted">No involved parties on this incident.</div>}
        <ul className="space-y-2">
          {visible.map((row, i) => {
            const n = i + 1;
            const key = row._key as string;
            const ptype = str(row.partytype).toUpperCase();
            return (
              <RowCard key={key} n={n} title={ptype || 'Party'} onRemove={() => remove(key)} removeLabel={`Remove party ${n}`}>
                <Sel label={`Party Type ${n}`} value={row.partytype} options={typeOpts} allowBlank={false}
                  onChange={(v) => update(key, { partytype: v, ...(v !== 'GWI DRIVER' ? { driverrecno: 0 } : {}) })} />
                {ptype === 'GWI DRIVER' ? (
                  <Cell label={`Name ${n}`} className="col-span-2">
                    {/* Retyping the name by hand drops the picked driver (recno + unit) until a hit is picked again. */}
                    <DriverTypeahead ariaLabel={`Name ${n}`} value={str(row.invpartyname)}
                      onChange={(text) => update(key, { invpartyname: text, driverrecno: 0, unitnumber: '' })}
                      onPick={(hit) => update(key, { driverrecno: Number(hit.driverrecno) || 0, unitnumber: str(hit.unitnumber), invpartyname: str(hit.drivername).trim() })} />
                    {Number(row.driverrecno) > 0 && <span className="text-xs text-muted">{driverLabel({ unitnumber: row.unitnumber, drivername: row.invpartyname })}</span>}
                  </Cell>
                ) : GWI_USER_PARTY_TYPES.has(ptype) ? (
                  <Cell label={`Name ${n}`} className="col-span-2">
                    <PermissionAwareInput aria-label={`Name ${n}`} className={cell} value={str(row.invpartyname)} onChange={(e) => update(key, { invpartyname: e.target.value })} />
                    {canEdit && (
                      <div className="mt-1">
                        <UserPicker value="" placeholder="Pick a GWI user…" emailDomain="@gwii.com" showEmail={false}
                          onChange={(_oid, user) => { if (user) update(key, { invpartyname: user.display_name }); }} />
                      </div>
                    )}
                  </Cell>
                ) : (
                  <Text label={`Name ${n}`} value={row.invpartyname} onChange={(v) => update(key, { invpartyname: v })} className="col-span-2" />
                )}
                <Text label={`Unit ${n}`} value={row.unitnumber} onChange={(v) => update(key, { unitnumber: v })} />
                <Text label={`License # ${n}`} value={row.invlicensenum} onChange={(v) => update(key, { invlicensenum: v })} />
                <Text label={`License State ${n}`} value={row.invlicensestate} onChange={(v) => update(key, { invlicensestate: v })} maxLength={2} />
                <Text label={`Address ${n}`} value={row.invadd} onChange={(v) => update(key, { invadd: v })} className="col-span-2" />
                <Text label={`City ${n}`} value={row.invcity} onChange={(v) => update(key, { invcity: v })} />
                <Text label={`State ${n}`} value={row.invstate} onChange={(v) => update(key, { invstate: v })} maxLength={2} />
                <Text label={`Zip ${n}`} value={row.invzip} onChange={(v) => update(key, { invzip: v })} maxLength={10} />
                <Text label={`Phone ${n}`} value={row.invphonenum} onChange={(v) => update(key, { invphonenum: v })} />
                <Yn label={`Citation ${n}`} value={row.invcitation} onChange={(v) => update(key, { invcitation: v })} />
                <Yn label={`Injured ${n}`} value={row.invinjured} onChange={(v) => update(key, { invinjured: v })} />
                <Yn label={`Ambulance ${n}`} value={row.invambulance} onChange={(v) => update(key, { invambulance: v })} />
                <Yn label={`Hospital ${n}`} value={row.invhospital} onChange={(v) => update(key, { invhospital: v })} />
                <Yn label={`Fatality ${n}`} value={row.invfatality} onChange={(v) => update(key, { invfatality: v })} />
                <Cell label={`Injury Description ${n}`} className="col-span-2 md:col-span-4 xl:col-span-6">
                  <PermissionAwareTextarea aria-label={`Injury Description ${n}`} rows={2} className={cell} value={str(row.invinjurydesc)}
                    onChange={(e) => update(key, { invinjurydesc: e.target.value })} />
                </Cell>
              </RowCard>
            );
          })}
        </ul>
        <AddButton label="Add party" onClick={onAdd} />
      </div>
    </PermissionAccessProvider>
  );
}

// ---- Equipment & cargo ------------------------------------------------------

export function EquipmentTab({ rows, onRowsChange, canEdit, onAdd }: RowsTabProps) {
  const visible = live(rows);
  const { update, remove } = useRowOps(rows, onRowsChange);
  const typeOpts = EQUIP_TYPES.map((v) => ({ value: v, label: v }));
  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="space-y-2">
        {visible.length === 0 && <div className="py-4 text-center text-sm text-muted">No equipment or cargo on this incident.</div>}
        <ul className="space-y-2">
          {visible.map((row, i) => {
            const n = i + 1;
            const key = row._key as string;
            const etype = str(row.equiptype).toUpperCase();
            const docs = row.recno ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-accent-text hover:underline">Photos / documents</summary>
                <div className="mt-1"><SafetyDocuments tablename="incident_equipment" tablerecno={Number(row.recno)} isReadOnly={!canEdit} /></div>
              </details>
            ) : undefined;
            return (
              <RowCard key={key} n={n} title={etype || 'Equipment'} onRemove={() => remove(key)} removeLabel={`Remove equipment ${n}`} extra={docs}>
                <Sel label={`Equip Type ${n}`} value={row.equiptype} options={typeOpts} allowBlank={false} onChange={(v) => update(key, { equiptype: v })} />
                {etype === 'GWI TRUCK' ? (
                  <Cell label={`Unit ${n}`}>
                    <UnitTypeahead ariaLabel={`Unit ${n}`} value={str(row.unitnumber)} onChange={(text) => update(key, { unitnumber: text })}
                      onPick={(hit) => update(key, {
                        unitnumber: str(hit.unitnumber), truckrecno: Number(hit.truckrecno) || 0, ownerrecno: Number(hit.ownerrecno) || 0,
                        equipowner: 'GWI', equipownername: str(hit.truckownername) || str(row.equipownername),
                      })} />
                  </Cell>
                ) : (
                  <Text label={`Unit ${n}`} value={row.unitnumber} onChange={(v) => update(key, { unitnumber: v })} />
                )}
                <Text label={`Owner ${n}`} value={row.equipowner} onChange={(v) => update(key, { equipowner: v })} />
                <Text label={`Owner Name ${n}`} value={row.equipownername} onChange={(v) => update(key, { equipownername: v })} />
                <Text label={`Equip Pro # ${n}`} value={Number(row.pronumber) > 0 ? row.pronumber : ''} onChange={(v) => update(key, { pronumber: v === '' ? 0 : v })} type="number" />
                <Yn label={`Hazmat ${n}`} value={row.hazmat} onChange={(v) => update(key, { hazmat: v })} />
                <Text label={`Description ${n}`} value={row.equipdescription} onChange={(v) => update(key, { equipdescription: v })} className="col-span-2" />
                <Text label={`VIN ${n}`} value={row.equipvin} onChange={(v) => update(key, { equipvin: v })} />
                <Yn label={`Damaged ${n}`} value={row.equipdamaged} onChange={(v) => update(key, { equipdamaged: v })} />
                <Yn label={`Towed ${n}`} value={row.equiptowed} onChange={(v) => update(key, { equiptowed: v })} />
                <Text label={`Towing Location ${n}`} value={row.towingloc} onChange={(v) => update(key, { towingloc: v })} />
                <Yn label={`Totaled ${n}`} value={row.equiptotaled} onChange={(v) => update(key, { equiptotaled: v })} />
                <DateCell label={`Owner Notified ${n}`} value={row.ownernotifieddate} onChange={(v) => update(key, { ownernotifieddate: v })} />
                <DateCell label={`Com Inv Rcvd ${n}`} value={row.cominvrcvddate} onChange={(v) => update(key, { cominvrcvddate: v })} />
                <Text label={`Insurance ${n}`} value={row.insurance} onChange={(v) => update(key, { insurance: v })} />
                <Text label={`Policy # ${n}`} value={row.insurancepolicynum} onChange={(v) => update(key, { insurancepolicynum: v })} />
                <Text label={`Phone ${n}`} value={row.equipphone} onChange={(v) => update(key, { equipphone: v })} />
                <Cell label={`Damages ${n}`} className="col-span-2 md:col-span-4 xl:col-span-6">
                  <PermissionAwareTextarea aria-label={`Damages ${n}`} rows={2} className={cell} value={str(row.damagesdesc)}
                    onChange={(e) => update(key, { damagesdesc: e.target.value })} />
                </Cell>
              </RowCard>
            );
          })}
        </ul>
        <AddButton label="Add equipment" onClick={onAdd} />
      </div>
    </PermissionAccessProvider>
  );
}

// ---- Claims -----------------------------------------------------------------

export interface ClaimsTabProps extends RowsTabProps {
  lookups: IncidentLookups;
}

export function ClaimsTab({ rows, onRowsChange, canEdit, onAdd, lookups }: ClaimsTabProps) {
  const visible = live(rows);
  const { update, remove } = useRowOps(rows, onRowsChange);
  const claimtypes = lookups?.claimtypes ?? [];
  const ctOpts = claimtypes.map((c) => ({ value: String(c.recno), label: c.claimtype }));
  const ptOpts = lookupOpts(lookups?.policytypes);
  const phOpts = lookupOpts(lookups?.policyholders);
  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="space-y-2">
        {visible.length === 0 && <div className="py-4 text-center text-sm text-muted">No claims on this incident.</div>}
        <ul className="space-y-2">
          {visible.map((row, i) => {
            const n = i + 1;
            const key = row._key as string;
            const title = str(row.claimnumber) || claimtypes.find((c) => String(c.recno) === str(row.claimtype))?.claimtype || 'Claim';
            // The label lives on the row (client-only key) so it survives leaving and re-entering the tab;
            // a loaded row shows the server's drivername until the user types / picks.
            const drv = row[CLAIM_DRIVER_LABEL_KEY] !== undefined ? str(row[CLAIM_DRIVER_LABEL_KEY]) : (Number(row.driverrecno) > 0 ? str(row.drivername) : '');
            return (
              <RowCard key={key} n={n} title={title} onRemove={() => remove(key)} removeLabel={`Remove claim ${n}`}>
                <Sel label={`Claim Type ${n}`} value={row.claimtype} options={ctOpts}
                  onChange={(v) => update(key, (r) => applyClaimType(r, claimtypes.find((c) => String(c.recno) === v) ?? null))} />
                <Yn label={`In House ${n}`} value={row.inhouse} onChange={(v) => update(key, { inhouse: v })} />
                <Text label={`Claim # ${n}`} value={row.claimnumber} onChange={(v) => update(key, { claimnumber: v })} />
                <Sel label={`Policy Type ${n}`} value={row.claimpolicytype} options={ptOpts} onChange={(v) => update(key, { claimpolicytype: v === '' ? 0 : Number(v) })} />
                <Sel label={`Policy Holder ${n}`} value={row.policyholder} options={phOpts} onChange={(v) => update(key, { policyholder: v === '' ? 0 : Number(v) })} />
                <Text label={`Policy # ${n}`} value={row.policynum} onChange={(v) => update(key, { policynum: v })} />
                <DateCell label={`Submit Date ${n}`} value={row.claimsubmitdate} onChange={(v) => update(key, { claimsubmitdate: v })} />
                <Text label={`Adjuster ${n}`} value={row.claimadjuster} onChange={(v) => update(key, { claimadjuster: v })} />
                <Text label={`Email ${n}`} value={row.claimemail} onChange={(v) => update(key, { claimemail: v })} />
                <Text label={`Phone ${n}`} value={row.claimphone} onChange={(v) => update(key, { claimphone: v })} />
                <Text label={`Check # ${n}`} value={row.claimchecknum} onChange={(v) => update(key, { claimchecknum: v })} />
                <Text label={`Claim Amount ${n}`} value={row.claimamount} onChange={(v) => update(key, { claimamount: v })} type="number" />
                <DateCell label={`Close Date ${n}`} value={row.claimclosedate} onChange={(v) => update(key, { claimclosedate: v })} />
                <Text label={`GWI Deductible ${n}`} value={row.claimgwideductible} onChange={(v) => update(key, { claimgwideductible: v })} type="number" />
                <Text label={`Driver Deductible ${n}`} value={row.claimdriverdeductible} onChange={(v) => update(key, { claimdriverdeductible: v })} type="number" />
                <Yn label={`Driver Paid ${n}`} value={row.claimdriverpaid} onChange={(v) => update(key, { claimdriverpaid: v })} />
                <Cell label={`Claim Driver ${n}`} className="col-span-2">
                  <DriverTypeahead ariaLabel={`Claim Driver ${n}`} value={drv}
                    onChange={(text) => update(key, { [CLAIM_DRIVER_LABEL_KEY]: text })}
                    onPick={(hit) => update(key, { [CLAIM_DRIVER_LABEL_KEY]: driverLabel(hit), driverrecno: Number(hit.driverrecno) || 0 })} />
                </Cell>
                <Text label={`Chargeback ${n}`} value={row.chargebacknum} onChange={(v) => update(key, { chargebacknum: v })} />
                <Text label={`Total Costs ${n}`} value={money(row.claimtotalcosts)} onChange={() => {}} readOnly />
                <Cell label={`Comments ${n}`} className="col-span-2 md:col-span-4 xl:col-span-6">
                  <PermissionAwareTextarea aria-label={`Comments ${n}`} rows={2} className={cell} value={str(row.claimcomments)}
                    onChange={(e) => update(key, { claimcomments: e.target.value })} />
                </Cell>
              </RowCard>
            );
          })}
        </ul>
        <AddButton label="Add claim" onClick={onAdd} />
      </div>
    </PermissionAccessProvider>
  );
}

// ---- Costs + claim-cost lines ----------------------------------------------

export interface CostsTabProps extends RowsTabProps {
  lookups: IncidentLookups;
  /** Saved claims of this incident (the Lines panel picks among them). */
  claims: Record<string, any>[];
  costlines: Record<string, any>[];
  incidentrecno: number | null;
  onLinesSaved: () => Promise<void> | void;
}

function CostLinesPanel({ incidentrecno, cost, claims, lines, onSaved, onClose, canEdit }: {
  incidentrecno: number; cost: ChildRow; claims: Record<string, any>[]; lines: Record<string, any>[];
  onSaved: () => Promise<void> | void; onClose: () => void; canEdit: boolean;
}) {
  const [rows, setRows] = useState<ChildRow[]>(() => lines.map((l) => ({ ...l, _key: `r-${l.recno}` })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const visible = live(rows);
  const update = (key: string, patch: Record<string, unknown>) => setRows((p) => p.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) => setRows((p) => p.flatMap((r) => (r._key !== key ? [r] : r.recno ? [{ ...r, deleted: 'Y' }] : [])));
  const total = visible.reduce((s, r) => s + num(r.cost), 0);
  const claimOpts = claims.map((c) => ({ value: String(c.recno), label: `${str(c.claimnumber) || `Claim ${c.recno}`}${c.claimtypename ? ` — ${c.claimtypename}` : ''}` }));

  const save = async () => {
    const body: CostLineRow[] = rows.map((r) => {
      if (str(r.deleted).toUpperCase() === 'Y') return { recno: r.recno, claimrecno: Number(r.claimrecno), cost: r.cost, glcode: str(r.glcode), deleted: 'Y' };
      const line: CostLineRow = { claimrecno: Number(r.claimrecno) || 0, cost: num(r.cost), glcode: str(r.glcode) };
      if (r.recno) line.recno = r.recno;
      return line;
    });
    setSaving(true);
    setError('');
    try {
      await safetySystemService.saveCostLines(incidentrecno, Number(cost.recno), body);
      await onSaved();
      onClose();
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="group" aria-label={`Cost lines for cost ${cost.recno}`} className="mt-2 rounded-md border border-accent/40 bg-highlight/40 p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">{`Claim cost lines — ${str(cost.invoice) || `cost ${cost.recno}`}`}</span>
        <span className="text-xs text-muted">{`Lines total ${money(total)}`}</span>
      </div>
      {error && <div role="alert" className="mb-2 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}
      <table className="w-full text-sm">
        <thead><tr><th className={th}>Claim</th><th className={th}>Cost</th><th className={th}>GL Code</th><th className={th} /></tr></thead>
        <tbody>
          {visible.length === 0 && <tr><td colSpan={4} className="px-2 py-2 text-center text-xs text-muted">No lines. Add one to split this cost across claims.</td></tr>}
          {visible.map((r, i) => {
            const n = i + 1;
            const key = r._key as string;
            return (
              <tr key={key}>
                <td className={td}>
                  <PermissionAwareSelect aria-label={`Line Claim ${n}`} className={cell} value={Number(r.claimrecno) > 0 ? str(r.claimrecno) : ''}
                    onChange={(e) => update(key, { claimrecno: e.target.value === '' ? 0 : Number(e.target.value) })}>
                    <option value="">—</option>
                    {claimOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </PermissionAwareSelect>
                </td>
                <td className={td}><PermissionAwareInput type="number" aria-label={`Line Cost ${n}`} className={cell} value={str(r.cost)} onChange={(e) => update(key, { cost: e.target.value })} /></td>
                <td className={td}><PermissionAwareInput aria-label={`Line GL Code ${n}`} className={cell} value={str(r.glcode)} onChange={(e) => update(key, { glcode: e.target.value })} /></td>
                <td className={td}>
                  <PermissionAwareButton type="button" aria-label={`Remove line ${n}`} onClick={() => remove(key)}
                    className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg disabled:cursor-not-allowed">
                    <TrashIcon width={16} aria-hidden="true" />
                  </PermissionAwareButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 flex items-center justify-between gap-2">
        <AddButton label="Add line" onClick={() => setRows((p) => [...p, newCostLine()])} />
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-rule px-3 py-1 text-sm hover:bg-highlight">Close</button>
          {canEdit && (
            <button type="button" onClick={save} disabled={saving}
              className="rounded-md bg-accent px-3 py-1 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400">
              {saving ? 'Saving…' : 'Save Lines'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CostsTab({ rows, onRowsChange, canEdit, onAdd, lookups, claims, costlines, incidentrecno, onLinesSaved }: CostsTabProps) {
  const visible = live(rows);
  const { update, remove } = useRowOps(rows, onRowsChange);
  const ctOpts = lookupOpts(lookups?.costtypes);
  const paidOpts = lookupOpts(lookups?.paidto);
  const payOpts = lookupOpts(lookups?.paymenttypes);
  const [linesFor, setLinesFor] = useState<string | null>(null);
  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="space-y-2">
        {visible.length === 0 && <div className="py-4 text-center text-sm text-muted">No costs on this incident.</div>}
        <ul className="space-y-2">
          {visible.map((row, i) => {
            const n = i + 1;
            const key = row._key as string;
            const lines = row.recno ? costLinesFor(costlines, Number(row.recno)) : [];
            const hasLines = lines.length > 0;
            const title = lookups?.costtypes?.find((c) => String(c.recno) === str(row.costtype))?.label || str(row.invoice) || 'Cost';
            const panel = row.recno && incidentrecno !== null && linesFor === key ? (
              <CostLinesPanel incidentrecno={incidentrecno} cost={row} claims={claims} lines={lines} canEdit={canEdit}
                onSaved={onLinesSaved} onClose={() => setLinesFor(null)} />
            ) : undefined;
            return (
              <RowCard key={key} n={n} title={title} onRemove={() => remove(key)} removeLabel={`Remove cost ${n}`} extra={panel}>
                <Sel label={`Cost Type ${n}`} value={row.costtype} options={ctOpts} onChange={(v) => update(key, { costtype: v === '' ? 0 : Number(v) })} />
                <Text label={`Cost Description ${n}`} value={row.costdesc} onChange={(v) => update(key, { costdesc: v })} className="col-span-2" />
                <Sel label={`Paid To ${n}`} value={row.paidtorecno} options={paidOpts} onChange={(v) => update(key, { paidtorecno: v === '' ? 0 : Number(v) })} />
                <Text label={`Invoice ${n}`} value={row.invoice} onChange={(v) => update(key, { invoice: v })} />
                <Cell label={`Total Cost ${n}`}>
                  <div className="flex items-center gap-1">
                    <PermissionAwareInput type="number" aria-label={`Total Cost ${n}`} className={cell} value={str(row.totalaccost)} readOnly={hasLines} disabled={hasLines}
                      onChange={(e) => update(key, { totalaccost: e.target.value })} />
                    {row.recno && (
                      <button type="button" aria-label={`Lines for cost ${n}`} onClick={() => setLinesFor(linesFor === key ? null : key)}
                        className="whitespace-nowrap rounded-md border border-rule px-2 py-1 text-xs hover:bg-highlight">
                        {`Lines…${hasLines ? ` (${lines.length})` : ''}`}
                      </button>
                    )}
                  </div>
                </Cell>
                <Sel label={`Payment Type ${n}`} value={row.paymenttype} options={payOpts} onChange={(v) => update(key, { paymenttype: v === '' ? 0 : Number(v) })} />
                <Text label={`Check # ${n}`} value={row.checknum} onChange={(v) => update(key, { checknum: v })} />
                <DateCell label={`Paid Date ${n}`} value={row.acpaiddate} onChange={(v) => update(key, { acpaiddate: v })} />
              </RowCard>
            );
          })}
        </ul>
        <AddButton label="Add cost" onClick={onAdd} />
      </div>
    </PermissionAccessProvider>
  );
}

// ---- Tickets (link / unlink) -----------------------------------------------

export function TicketsTab({ recno, tickets, canEdit, onChanged }: {
  recno: number; tickets: Record<string, any>[]; canEdit: boolean; onChanged: () => Promise<void> | void;
}) {
  const [ticketnum, setTicketnum] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const link = async () => {
    const n = ticketnum.trim();
    if (!n) return;
    setBusy(true);
    setError('');
    try {
      await safetySystemService.linkTicket(recno, n);
      setTicketnum('');
      await onChanged();
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const unlink = async (ticketrecno: number) => {
    setBusy(true);
    setError('');
    try {
      await safetySystemService.unlinkTicket(recno, ticketrecno);
      await onChanged();
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {error && <div role="alert" className="rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>}
      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <span className={labelCls}>Add by ticket #</span>
            <input aria-label="Ticket number to link" className={`${inputClass} w-48`} value={ticketnum} onChange={(e) => setTicketnum(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') link(); }} placeholder="Ticket #…" />
          </div>
          <button type="button" onClick={link} disabled={busy || !ticketnum.trim()}
            className="rounded-md bg-accent px-3 py-1 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400">
            Link ticket
          </button>
        </div>
      )}
      {tickets.length === 0 ? (
        <div className="py-4 text-center text-sm text-muted">No tickets linked to this incident.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={th}>Ticket #</th><th className={th}>Date</th><th className={th}>Officer</th><th className={th}>County</th><th className={th}>Level</th>
                <th className={th}>Weight</th><th className={th}>Violations</th><th className={th}>Equipment</th><th className={th}>Minutes</th><th className={th}>Status</th><th className={th} />
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const closed = str(t.closedatetime) && !str(t.closedatetime).startsWith('0000');
                return (
                  <tr key={t.recno} className="border-t border-rule">
                    <td className={td}>{str(t.ticketnum)}</td>
                    <td className={`${td} whitespace-nowrap`}>{shortDate(t.ticketstartdate)}</td>
                    <td className={td}>{str(t.offdisplayname)}</td>
                    <td className={td}>{str(t.ticketcounty)}</td>
                    <td className={td}>{str(t.ticketlevel)}</td>
                    <td className={td}>{str(t.totticketweight)}</td>
                    <td className={td}>{str(t.violcount)}</td>
                    <td className={td}>{str(t.equipcount)}</td>
                    <td className={td}>{str(t.inspectionminutes)}</td>
                    <td className={td}>{closed ? 'CLOSED' : 'OPEN'}</td>
                    <td className={td}>
                      {canEdit && (
                        <button type="button" aria-label={`Remove ticket ${str(t.ticketnum)}`} onClick={() => unlink(Number(t.recno))} disabled={busy}
                          className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg disabled:cursor-not-allowed">
                          <TrashIcon width={16} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
